import {
  useCreateLifeArea,
  useLifeAreaById,
  useUpdateLifeArea,
} from 'features/life-areas';
import { useIsFavorite, useToggleFavorite } from 'features/user';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { LifeAreaFormDialog } from 'shared/dialogs/life-area-form';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { ID } from 'shared/types/id';
import { LifeArea, NewLifeArea } from 'shared/types/life-area';
import { LifeAreaFormFields } from 'shared/types/life-area-form';
import { LimitKeys } from 'shared/types/limit-keys';
import { FavoriteType } from 'shared/types/user-settings';

import {
  CreateLifeAreaContextProps,
  LifeAreaFormContext,
} from './life-area-form-context';

export type LifeAreaFormProviderProps = {
  children: React.ReactNode;
};

export const LifeAreaFormProvider: React.FC<LifeAreaFormProviderProps> = ({
  children,
}) => {
  // useOpenPremiumDialog is part of a higher level context which should be kept in mind (probably different setup needed)
  const openPremiumDialog = useOpenPremiumDialog();
  const { allowed: editLifeAreasAllowed } = usePremiumFeatureAllowed(
    LimitKeys.EditLifeAreas,
    {},
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<LifeAreaFormFields>();
  const [lifeAreaId, setLifeAreaId] = useState<ID>();
  const lifeArea = useLifeAreaById(lifeAreaId);
  const create = useCreateLifeArea();
  const update = useUpdateLifeArea();
  const isFavorite = useIsFavorite(lifeAreaId);
  const toggleFavorite = useToggleFavorite();
  const [pendingFavorite, setPendingFavorite] = useState(false);
  const { retry, isLoading, error, isSuccess, reset } = useMemo(
    () => (lifeAreaId ? update : create),
    [create, lifeAreaId, update],
  );

  const dialogInitialValues = useMemo<LifeAreaFormFields | undefined>(
    () =>
      initialValues ? { ...initialValues, favorite: isFavorite } : undefined,
    [initialValues, isFavorite],
  );

  const openDialog = useCallback(
    (initialValues?: LifeAreaFormFields, id?: ID) => {
      setLifeAreaId(id);
      setInitialValues(initialValues);
      setDialogOpen(true);
    },
    [setDialogOpen],
  );

  const closeDialog = useCallback(() => {
    reset();
    setInitialValues(undefined);
    setLifeAreaId(undefined);
    setDialogOpen(false);
  }, [setDialogOpen, reset]);

  const submitForm = useCallback(
    (values: LifeAreaFormFields) => {
      if (!values.name) {
        return;
      }

      const { favorite, ...lifeAreaValues } = values;

      if (lifeArea) {
        update.submit({
          ...lifeAreaValues,
          id: lifeArea.id,
          templateId: null,
        } as LifeArea);
        if (isFavorite !== favorite) {
          toggleFavorite(lifeArea.id, FavoriteType.LifeArea);
        }
        return;
      }

      setPendingFavorite(!!favorite);
      return create.submit(lifeAreaValues as NewLifeArea);
    },
    [create, isFavorite, lifeArea, toggleFavorite, update],
  );

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    if (pendingFavorite && create.data?.id) {
      toggleFavorite(create.data.id, FavoriteType.LifeArea);
    }
    setPendingFavorite(false);
    closeDialog();
  }, [closeDialog, create.data, isSuccess, pendingFavorite, toggleFavorite]);

  const value = useMemo<CreateLifeAreaContextProps>(
    () => ({ onCreateLifeArea: openDialog, onEditLifeArea: openDialog }),
    [openDialog],
  );

  return (
    <LifeAreaFormContext.Provider value={value}>
      {children}
      <LifeAreaFormDialog
        initialValues={dialogInitialValues}
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={submitForm}
        onRetry={retry}
        isLoading={isLoading}
        isError={!!error}
        isEdit={!!lifeAreaId}
        isCustomIconAllowed={!lifeArea || editLifeAreasAllowed}
        onPremium={openPremiumDialog}
      />
    </LifeAreaFormContext.Provider>
  );
};
